import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Layout as LayoutIcon, Plus, Edit, Trash2, Eye, EyeOff, GripVertical, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SECTION_TYPES = [
  { value: 'hero', label: 'Hero Banner', description: 'Large header with CTA' },
  { value: 'features', label: 'Features', description: 'Feature grid or list' },
  { value: 'stats', label: 'Statistics', description: 'Key metrics display' },
  { value: 'cta', label: 'Call to Action', description: 'Action prompt section' },
  { value: 'content', label: 'Content Block', description: 'Text and media' },
  { value: 'custom', label: 'Custom', description: 'Custom section type' },
];

export const ContentManager = () => {
  const [loading, setLoading] = useState(true);
  const [sections, setSections] = useState([]);
  const [pages, setPages] = useState([]);
  const [selectedPage, setSelectedPage] = useState('all');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedSection, setSelectedSection] = useState(null);
  const [formData, setFormData] = useState({
    page_key: '',
    section_key: '',
    section_type: 'hero',
    title: '',
    is_enabled: true,
    order: 0,
    content: {
      heading: '',
      subheading: '',
      body_text: '',
      cta_text: '',
      cta_link: '',
      image_url: '',
      icon: '',
      badge_text: '',
    }
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [sectionsRes, pagesRes] = await Promise.all([
        axios.get(`${API}/sections`),
        axios.get(`${API}/pages`)
      ]);
      
      setSections(sectionsRes.data);
      setPages(pagesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load content sections');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSection = () => {
    setFormData({
      page_key: selectedPage !== 'all' ? selectedPage : '',
      section_key: '',
      section_type: 'hero',
      title: '',
      is_enabled: true,
      order: sections.filter(s => s.page_key === selectedPage).length,
      content: {
        heading: '',
        subheading: '',
        body_text: '',
        cta_text: '',
        cta_link: '',
        image_url: '',
        icon: '',
        badge_text: '',
      }
    });
    setCreateDialogOpen(true);
  };

  const handleEditSection = (section) => {
    setSelectedSection(section);
    setFormData({
      page_key: section.page_key,
      section_key: section.section_key,
      section_type: section.section_type,
      title: section.title,
      is_enabled: section.is_enabled,
      order: section.order,
      content: section.content || {}
    });
    setEditDialogOpen(true);
  };

  const handleFormChange = (field, value) => {
    if (field.startsWith('content.')) {
      const contentField = field.replace('content.', '');
      setFormData({
        ...formData,
        content: {
          ...formData.content,
          [contentField]: value
        }
      });
    } else {
      setFormData({
        ...formData,
        [field]: value
      });
    }
  };

  const handleSaveSection = async () => {
    try {
      await axios.put(`${API}/sections/${selectedSection.id}`, formData);
      toast.success('Section updated successfully');
      setEditDialogOpen(false);
      await fetchData();
    } catch (error) {
      console.error('Error updating section:', error);
      toast.error('Failed to update section');
    }
  };

  const handleCreateNewSection = async () => {
    try {
      if (!formData.page_key) {
        toast.error('Please select a page');
        return;
      }
      if (!formData.section_key) {
        toast.error('Please enter a section key');
        return;
      }

      await axios.post(`${API}/sections`, formData);
      toast.success('Section created successfully');
      setCreateDialogOpen(false);
      await fetchData();
    } catch (error) {
      console.error('Error creating section:', error);
      toast.error(error.response?.data?.detail || 'Failed to create section');
    }
  };

  const handleDeleteSection = async (section) => {
    if (!window.confirm(`Are you sure you want to delete "${section.title}"?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/sections/${section.id}`);
      toast.success('Section deleted successfully');
      await fetchData();
    } catch (error) {
      console.error('Error deleting section:', error);
      toast.error('Failed to delete section');
    }
  };

  const handleToggleEnabled = async (section) => {
    try {
      await axios.put(`${API}/sections/${section.id}`, {
        is_enabled: !section.is_enabled
      });
      toast.success(`Section ${!section.is_enabled ? 'enabled' : 'disabled'}`);
      await fetchData();
    } catch (error) {
      console.error('Error toggling section:', error);
      toast.error('Failed to update section');
    }
  };

  const getFilteredSections = () => {
    if (selectedPage === 'all') return sections;
    return sections.filter(s => s.page_key === selectedPage);
  };

  const groupedSections = () => {
    const filtered = getFilteredSections();
    const grouped = {};
    
    filtered.forEach(section => {
      if (!grouped[section.page_key]) {
        grouped[section.page_key] = [];
      }
      grouped[section.page_key].push(section);
    });

    return grouped;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-muted-foreground">Loading content manager...</p>
          </div>
        </div>
      </Layout>
    );
  }

  const grouped = groupedSections();

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <LayoutIcon className="h-8 w-8 text-blue-600" />
            <div>
              <h1 className="text-3xl font-bold">Content Manager</h1>
              <p className="text-muted-foreground">Manage page sections and content</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={fetchData}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            <Button onClick={handleCreateSection}>
              <Plus className="h-4 w-4 mr-2" />
              New Section
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Total Sections</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{sections.length}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Enabled</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">
                {sections.filter(s => s.is_enabled).length}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Disabled</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">
                {sections.filter(s => !s.is_enabled).length}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Pages with Sections</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">
                {Object.keys(grouped).length}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filter */}
        <Card>
          <CardHeader>
            <CardTitle>Filter by Page</CardTitle>
          </CardHeader>
          <CardContent>
            <Select value={selectedPage} onValueChange={setSelectedPage}>
              <SelectTrigger className="w-80">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Pages</SelectItem>
                {pages.map(page => (
                  <SelectItem key={page.page_key} value={page.page_key}>
                    {page.title} ({page.path})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {/* Sections List */}
        {selectedPage === 'all' ? (
          // Grouped by page
          <div className="space-y-6">
            {Object.entries(grouped).map(([pageKey, pageSections]) => {
              const page = pages.find(p => p.page_key === pageKey);
              return (
                <Card key={pageKey}>
                  <CardHeader>
                    <CardTitle>{page?.title || pageKey}</CardTitle>
                    <CardDescription>{page?.path}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <SectionList
                      sections={pageSections}
                      onEdit={handleEditSection}
                      onDelete={handleDeleteSection}
                      onToggle={handleToggleEnabled}
                    />
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          // Single page view
          <Card>
            <CardHeader>
              <CardTitle>
                {pages.find(p => p.page_key === selectedPage)?.title || 'Sections'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {getFilteredSections().length > 0 ? (
                <SectionList
                  sections={getFilteredSections()}
                  onEdit={handleEditSection}
                  onDelete={handleDeleteSection}
                  onToggle={handleToggleEnabled}
                />
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  No sections found. Create your first section!
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Create Dialog */}
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create New Section</DialogTitle>
              <DialogDescription>Add a new content section to a page</DialogDescription>
            </DialogHeader>

            <SectionForm formData={formData} onChange={handleFormChange} pages={pages} isCreate={true} />

            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateNewSection}>
                Create Section
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Section: {selectedSection?.title}</DialogTitle>
              <DialogDescription>Update section content and settings</DialogDescription>
            </DialogHeader>

            <SectionForm formData={formData} onChange={handleFormChange} pages={pages} isCreate={false} />

            <DialogFooter>
              <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveSection}>
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

const SectionList = ({ sections, onEdit, onDelete, onToggle }) => {
  return (
    <div className="space-y-3">
      {sections.map(section => (
        <div
          key={section.id}
          className="flex items-center justify-between p-4 border rounded-lg hover:bg-accent/50 transition-colors"
        >
          <div className="flex items-center gap-4 flex-1">
            <GripVertical className="h-5 w-5 text-muted-foreground cursor-move" />
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <Badge variant={section.is_enabled ? 'default' : 'secondary'}>
                  {section.is_enabled ? <Eye className="h-3 w-3 mr-1" /> : <EyeOff className="h-3 w-3 mr-1" />}
                  {section.is_enabled ? 'Enabled' : 'Disabled'}
                </Badge>
                <Badge variant="outline">{section.section_type}</Badge>
                <span className="font-medium">{section.title}</span>
              </div>
              <div className="text-sm text-muted-foreground">
                Key: {section.section_key} • Order: {section.order}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="ghost" onClick={() => onToggle(section)}>
              {section.is_enabled ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => onEdit(section)}>
              <Edit className="h-4 w-4" />
            </Button>
            <Button size="sm" variant="ghost" onClick={() => onDelete(section)}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
};

const SectionForm = ({ formData, onChange, pages, isCreate }) => {
  return (
    <div className="space-y-4">
      {/* Basic Info */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Page *</Label>
          <Select
            value={formData.page_key}
            onValueChange={(val) => onChange('page_key', val)}
            disabled={!isCreate}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select page" />
            </SelectTrigger>
            <SelectContent>
              {pages.map(page => (
                <SelectItem key={page.page_key} value={page.page_key}>
                  {page.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>Section Type *</Label>
          <Select
            value={formData.section_type}
            onValueChange={(val) => onChange('section_type', val)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SECTION_TYPES.map(type => (
                <SelectItem key={type.value} value={type.value}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Section Key * (unique identifier)</Label>
          <Input
            value={formData.section_key}
            onChange={(e) => onChange('section_key', e.target.value)}
            placeholder="hero-banner"
            disabled={!isCreate}
          />
        </div>

        <div className="space-y-2">
          <Label>Display Title *</Label>
          <Input
            value={formData.title}
            onChange={(e) => onChange('title', e.target.value)}
            placeholder="Hero Section"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Display Order</Label>
          <Input
            type="number"
            value={formData.order}
            onChange={(e) => onChange('order', parseInt(e.target.value))}
          />
        </div>

        <div className="flex items-center space-x-2 pt-8">
          <Switch
            checked={formData.is_enabled}
            onCheckedChange={(checked) => onChange('is_enabled', checked)}
          />
          <Label>Section Enabled</Label>
        </div>
      </div>

      {/* Content Fields */}
      <div className="border-t pt-4 mt-4">
        <h3 className="text-lg font-semibold mb-4">Content</h3>
        
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Heading</Label>
            <Input
              value={formData.content.heading || ''}
              onChange={(e) => onChange('content.heading', e.target.value)}
              placeholder="Main heading text"
            />
          </div>

          <div className="space-y-2">
            <Label>Subheading</Label>
            <Input
              value={formData.content.subheading || ''}
              onChange={(e) => onChange('content.subheading', e.target.value)}
              placeholder="Subheading or tagline"
            />
          </div>

          <div className="space-y-2">
            <Label>Body Text</Label>
            <Textarea
              value={formData.content.body_text || ''}
              onChange={(e) => onChange('content.body_text', e.target.value)}
              placeholder="Main content text"
              rows={4}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>CTA Button Text</Label>
              <Input
                value={formData.content.cta_text || ''}
                onChange={(e) => onChange('content.cta_text', e.target.value)}
                placeholder="Get Started"
              />
            </div>

            <div className="space-y-2">
              <Label>CTA Link</Label>
              <Input
                value={formData.content.cta_link || ''}
                onChange={(e) => onChange('content.cta_link', e.target.value)}
                placeholder="/signup"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Image URL</Label>
              <Input
                value={formData.content.image_url || ''}
                onChange={(e) => onChange('content.image_url', e.target.value)}
                placeholder="https://example.com/image.jpg"
              />
            </div>

            <div className="space-y-2">
              <Label>Icon Name</Label>
              <Input
                value={formData.content.icon || ''}
                onChange={(e) => onChange('content.icon', e.target.value)}
                placeholder="Star"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Badge Text</Label>
            <Input
              value={formData.content.badge_text || ''}
              onChange={(e) => onChange('content.badge_text', e.target.value)}
              placeholder="New Feature"
            />
          </div>
        </div>
      </div>
    </div>
  );
};
